export const emptyPermissionScope = {
  projects: { read: [], upload: [], edit: [] },
  collections: { read: [], upload: [], edit: [] },
  storage: { read: false, write: false }
};

export const normalizeIdList = (values) => {
  if (!Array.isArray(values)) return [];

  const numericValues = values
    .map((value) => Number(value))
    .filter((parsed) => Number.isInteger(parsed) && parsed > 0);

  return Array.from(new Set(numericValues));
};

export const normalizeBooleanFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
};

export const normalizePermissionScope = (scope) => {
  const normalized = {
    projects: { read: [], upload: [], edit: [] },
    collections: { read: [], upload: [], edit: [] },
    storage: { read: false, write: false }
  };
  if (!scope || typeof scope !== 'object') return normalized;

  normalized.projects.read = normalizeIdList(scope?.projects?.read);
  normalized.projects.upload = normalizeIdList(scope?.projects?.upload);
  normalized.projects.edit = normalizeIdList(scope?.projects?.edit);
  normalized.collections.read = normalizeIdList(scope?.collections?.read);
  normalized.collections.upload = normalizeIdList(scope?.collections?.upload);
  normalized.collections.edit = normalizeIdList(scope?.collections?.edit);
  normalized.storage.read = normalizeBooleanFlag(scope?.storage?.read);
  normalized.storage.write = normalizeBooleanFlag(scope?.storage?.write);
  if (normalized.storage.write) {
    normalized.storage.read = true;
  }

  ['projects', 'collections'].forEach((resource) => {
    normalized[resource].edit.forEach((id) => {
      if (!normalized[resource].upload.includes(id)) {
        normalized[resource].upload.push(id);
      }
      if (!normalized[resource].read.includes(id)) {
        normalized[resource].read.push(id);
      }
    });
  });

  return normalized;
};

export const scopeHasAnyEntry = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return (
    normalized.projects.read.length > 0 ||
    normalized.projects.upload.length > 0 ||
    normalized.projects.edit.length > 0 ||
    normalized.collections.read.length > 0 ||
    normalized.collections.upload.length > 0 ||
    normalized.collections.edit.length > 0 ||
    normalized.storage.read ||
    normalized.storage.write
  );
};

export const getProjectScopedAccessLevel = (scope, projectId) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return 'none';
  }
  if (normalized.projects.edit.includes(normalizedProjectId)) {
    return 'edit';
  }
  if (normalized.projects.upload.includes(normalizedProjectId)) {
    return 'upload';
  }
  if (normalized.projects.read.includes(normalizedProjectId)) {
    return 'read';
  }
  return 'none';
};

export const getProjectScopedAccessFlags = (scope, projectId) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return { read: false, upload: false, edit: false };
  }

  return {
    read: normalized.projects.read.includes(normalizedProjectId),
    upload: normalized.projects.upload.includes(normalizedProjectId),
    edit: normalized.projects.edit.includes(normalizedProjectId)
  };
};

export const setProjectScopedAccessLevel = (scope, projectId, accessLevel) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return normalized;
  }

  ['read', 'upload', 'edit'].forEach((mode) => {
    normalized.projects[mode] = normalized.projects[mode].filter((id) => id !== normalizedProjectId);
  });

  if (accessLevel === 'read') {
    normalized.projects.read.push(normalizedProjectId);
  } else if (accessLevel === 'upload') {
    normalized.projects.upload.push(normalizedProjectId);
  } else if (accessLevel === 'edit') {
    normalized.projects.edit.push(normalizedProjectId);
  }

  return normalizePermissionScope(normalized);
};

export const setProjectScopedAccessFlags = (scope, projectId, accessFlags = {}) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return normalized;
  }

  const nextFlags = {
    read: Boolean(accessFlags.read),
    upload: Boolean(accessFlags.upload),
    edit: Boolean(accessFlags.edit)
  };

  if (nextFlags.edit) {
    nextFlags.read = true;
    nextFlags.upload = true;
  }

  if (!nextFlags.upload) {
    nextFlags.edit = false;
  }

  ['read', 'upload', 'edit'].forEach((mode) => {
    normalized.projects[mode] = normalized.projects[mode].filter((id) => id !== normalizedProjectId);
  });

  if (nextFlags.read) {
    normalized.projects.read.push(normalizedProjectId);
  }
  if (nextFlags.upload) {
    normalized.projects.upload.push(normalizedProjectId);
  }
  if (nextFlags.edit) {
    normalized.projects.edit.push(normalizedProjectId);
  }

  return normalizePermissionScope(normalized);
};

export const getStorageAccessFlags = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return {
    read: Boolean(normalized.storage.read || normalized.storage.write),
    write: Boolean(normalized.storage.write)
  };
};

export const setStorageAccessFlags = (scope, accessFlags = {}) => {
  const normalized = normalizePermissionScope(scope);
  const nextFlags = {
    read: normalizeBooleanFlag(accessFlags.read),
    write: normalizeBooleanFlag(accessFlags.write)
  };

  if (nextFlags.write) {
    nextFlags.read = true;
  }

  normalized.storage.read = nextFlags.read;
  normalized.storage.write = nextFlags.write;

  return normalizePermissionScope(normalized);
};

export const getEffectiveProjectAccessLevel = (userRow, projectId) => {
  const permission = String(userRow?.permission || '').trim().toLowerCase();
  if (permission === 'admin' || permission === 'editor') {
    return permission;
  }
  return getProjectScopedAccessLevel(userRow?.permission_scope, projectId);
};

export const getEffectiveProjectAccessFlags = (userRow, projectId) => {
  const permission = String(userRow?.permission || '').trim().toLowerCase();
  if (permission === 'admin' || permission === 'editor') {
    return { read: true, upload: true, edit: true };
  }
  return getProjectScopedAccessFlags(userRow?.permission_scope, projectId);
};

export const getEffectiveStorageAccessFlags = (userRow) => {
  const permission = String(userRow?.permission || '').trim().toLowerCase();
  if (permission === 'admin' || permission === 'editor') {
    return { read: true, write: true };
  }
  return getStorageAccessFlags(userRow?.permission_scope);
};
